import { useEffect, useRef } from 'react';
import { Search, Filter } from 'lucide-react';
import { cn } from '../../lib/utils';
import { FileTree, type LoadChildrenFn } from './FileTree';
import { SyncStatusBar } from './SyncStatusBar';
import { CountBadge } from './CountBadge';
import type { FileNode, SyncStatusBarProps, FileExplorerFilterConfig, FileExplorerSearchConfig } from './types';

export interface FileExplorerProps {
  files: FileNode[];
  isLoading?: boolean;
  selectedFile?: FileNode | null;
  expandedFolders: Set<string>;
  onFileSelect?: (file: FileNode) => void;
  /**
   * Double-click on a file. VS Code's rule: a single click previews, a
   * double-click opens a tab that survives the next preview. Omit to keep
   * single-click-only behaviour.
   */
  onFileOpen?: (file: FileNode) => void;
  onToggleFolder?: (folderId: string) => void;
  /** Optional lazy-load callback for folder children. If not provided, uses file.children. */
  loadChildren?: LoadChildrenFn;
  onRefreshMetadataType?: (metadataType: string) => void;
  onAddFile?: (metadataType: string) => void;
  refreshingType?: string | null;
  isSyncInProgress?: boolean;
  /** Header title */
  title?: string;
  /** Filter button config. When provided, shows filter button in header. */
  filter?: FileExplorerFilterConfig;
  /** Item count badge in header (e.g. files.length). Shown when not loading and > 0. */
  itemCount?: number;
  /** Search hint bar. When provided, shows clickable search hint below header. */
  search?: FileExplorerSearchConfig;
  /** Sync status bar. When null/undefined, footer is hidden. */
  syncStatusBar?: SyncStatusBarProps | null;
  /** Custom empty state render (tree area when files.length === 0 and not loading) */
  renderEmptyState?: () => React.ReactNode;
  /** Custom loading state render */
  renderLoadingState?: () => React.ReactNode;
  className?: string;
  headerClassName?: string;
  treeClassName?: string;
  footerClassName?: string;
  /** Custom tree content. When provided, replaces built-in FileTree (files/tree props still required for layout). */
  children?: React.ReactNode;
  /**
   * When set, the tree scrolls to the node with this file ID (smooth scroll, nearest block).
   * Typically tied to the active editor tab's file ID so the explorer mirrors the editor.
   */
  revealFileId?: string;
  /** Called when the user clicks the copy-name button on a file node. */
  onCopyName?: (file: FileNode) => void;
}

function DefaultLoadingState() {
  return (
    <div className="space-y-1">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="space-y-2 px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 bg-muted rounded animate-pulse" />
            <div
              className="h-3 bg-muted rounded flex-1 animate-pulse"
              style={{ width: `${60 + Math.random() * 40}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function DefaultEmptyState() {
  return (
    <div className="text-xs text-muted-foreground p-3 text-center">
      No metadata types available
    </div>
  );
}

export function FileExplorer({
  files,
  isLoading = false,
  selectedFile,
  expandedFolders,
  onFileSelect,
  onFileOpen,
  onToggleFolder,
  loadChildren,
  onRefreshMetadataType,
  onAddFile,
  refreshingType,
  isSyncInProgress,
  title = 'Explorer',
  filter,
  itemCount,
  search,
  syncStatusBar,
  renderEmptyState,
  renderLoadingState,
  className,
  headerClassName,
  treeClassName,
  footerClassName,
  children: customTree,
  revealFileId,
  onCopyName,
}: FileExplorerProps) {
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const activeFilterCount = filter?.activeCount ?? 0;

  useEffect(() => {
    if (!revealFileId || !treeContainerRef.current) return;

    const container = treeContainerRef.current;
    const selector = `[data-tree-item-id="${CSS.escape(revealFileId)}"]`;

    // Try immediately — works when the folder is already expanded
    const el = container.querySelector<HTMLElement>(selector);
    if (el) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return;
    }

    // Element not in DOM yet — folder children are loading asynchronously.
    // Watch for the element to appear, then scroll to it.
    let timeoutId: ReturnType<typeof setTimeout>;
    const observer = new MutationObserver(() => {
      const found = container.querySelector<HTMLElement>(selector);
      if (found) {
        found.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        observer.disconnect();
        clearTimeout(timeoutId);
      }
    });

    observer.observe(container, { childList: true, subtree: true });
    // Disconnect after 5 s in case the file doesn't exist in the current tree
    timeoutId = setTimeout(() => observer.disconnect(), 5000);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [revealFileId]);
  const isFilterActive = filter?.isActive ?? (activeFilterCount > 0 && (filter?.totalCount ?? 0) > 0 && activeFilterCount < (filter?.totalCount ?? 0));

  return (
    <div className={cn('h-full flex flex-col bg-background', className)}>
      <div className={cn('px-3 py-2 border-b border-border', headerClassName)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          </div>
          <div className="flex items-center gap-1">
            {filter && (
              <button
                type="button"
                onClick={filter.onClick}
                title={filter.shortcutLabel ? `Filter (${filter.shortcutLabel})` : 'Filter'}
                className={cn(
                  'flex items-center gap-1 px-1.5 py-1 rounded transition-colors',
                  'hover:bg-muted',
                  isFilterActive && 'text-primary'
                )}
              >
                <div className="relative">
                  <Filter className="h-3.5 w-3.5" />
                  {activeFilterCount > 0 && (
                    <span
                      className={cn(
                        'absolute -top-1.5 -right-1.5 h-3.5 min-w-3.5 px-0.5 flex items-center justify-center text-[9px] font-bold rounded-full',
                        isFilterActive
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground border border-border'
                      )}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                {filter.shortcutLabel && (
                  <kbd className="hidden sm:inline-block px-1 py-0.5 text-[9px] text-muted-foreground bg-muted border border-border rounded">
                    {filter.shortcutLabel}
                  </kbd>
                )}
              </button>
            )}
            {!isLoading && itemCount != null && itemCount > 0 && (
              <CountBadge value={itemCount} />
            )}
          </div>
        </div>

        {search && (
          <button
            type="button"
            onClick={search.onClick}
            className="mt-2 w-full flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground bg-muted/50 hover:bg-muted rounded-md border border-border/50 transition-colors"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="flex-1 text-left">{search.placeholder ?? 'Search files...'}</span>
            {search.shortcutLabel && (
              <kbd className="px-1.5 py-0.5 text-[10px] bg-background border border-border rounded">
                {search.shortcutLabel}
              </kbd>
            )}
          </button>
        )}
      </div>

      <div
        ref={treeContainerRef}
        className={cn('flex-1 overflow-auto scroll-smooth bg-background px-2', treeClassName)}
      >
        {isLoading ? (
          renderLoadingState ? renderLoadingState() : <DefaultLoadingState />
        ) : customTree != null ? (
          customTree
        ) : files.length === 0 ? (
          renderEmptyState ? renderEmptyState() : <DefaultEmptyState />
        ) : (
          <FileTree
            files={files}
            selectedFile={selectedFile}
            expandedFolders={expandedFolders}
            onFileSelect={onFileSelect}
            onFileOpen={onFileOpen}
            onToggleFolder={onToggleFolder}
            loadChildren={loadChildren}
            onRefreshMetadataType={onRefreshMetadataType}
            onAddFile={onAddFile}
            refreshingType={refreshingType}
            isSyncInProgress={isSyncInProgress}
            onCopyName={onCopyName}
          />
        )}
      </div>

      {syncStatusBar != null && (
        <div className={footerClassName}>
          <SyncStatusBar {...syncStatusBar} />
        </div>
      )}
    </div>
  );
}
 